import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Plus, Pencil, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import { AdminNav } from "../components/site/AdminNav";
import { AgencyFormDialog } from "../components/site/AgencyFormDialog";
import { BulkHours } from "../components/site/BulkHours";
import { useAuth } from "../lib/auth";
import { api, apiError } from "../lib/api";

const LABEL = { boleteria: "Boletería", agencia: "Agencia", puntoenvio: "PuntoEnvío" };

export default function AdminAgencias() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(undefined);
  const [sel, setSel] = useState([]);
  const toggle = (id) => setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    api.get("/admin/agencies").then((r) => { if (alive) setItems(Array.isArray(r.data) ? r.data : []); }).catch((e) => toast.error(apiError(e)));
    return () => { alive = false; };
  }, [user]);

  if (user === null) return <div className="grid min-h-[60vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (user === false) return <Navigate to="/admin" replace />;
  if (user.role !== "admin") return <Navigate to="/admin/trafico" replace />;

  const saved = (a) => {
    setItems((prev) => (prev.some((x) => x.id === a.id) ? prev.map((x) => (x.id === a.id ? a : x)) : [a, ...prev]));
    setEditing(undefined);
  };
  const remove = async (a) => {
    if (!window.confirm(`¿Eliminar «${a.name}»?`)) return;
    try {
      await api.delete(`/admin/agencies/${a.id}`);
      setItems((prev) => prev.filter((x) => x.id !== a.id));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <section data-testid="admin-agencies-page" className="min-h-[75vh] bg-[var(--paper)] pb-24">
      <div className="mx-auto max-w-[1000px] px-5 pt-12 md:px-10">
        <AdminNav title="Agencias" />
        <button onClick={() => setEditing(null)} data-testid="admin-agency-add" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[var(--ink)] px-5 py-3 text-sm font-bold text-white hover:bg-[var(--forest)]">
          <Plus className="h-4 w-4" /> Agregar punto (PuntoEnvío, agencia o boletería)
        </button>
        <BulkHours
          selected={sel}
          total={(items ?? []).length}
          onToggleAll={() => setSel((p) => (p.length === (items ?? []).length ? [] : (items ?? []).map((a) => a.id)))}
          onApplied={(h) => { setItems((prev) => prev.map((a) => (sel.includes(a.id) ? { ...a, hours: h } : a))); setSel([]); }}
        />
        <div className="mt-6 space-y-3">
          {items === null && <div className="flex items-center gap-2 text-[var(--muted-ink)]"><Loader2 className="h-5 w-5 animate-spin" /> Cargando…</div>}
          {(items ?? []).map((a) => (
            <div key={a.id} data-testid={`admin-agency-${a.id}`} className="flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-white p-5 ring-1 ring-black/5">
              <label className="flex min-w-0 flex-1 basis-full cursor-pointer items-start gap-4 sm:basis-auto">
              <input type="checkbox" checked={sel.includes(a.id)} onChange={() => toggle(a.id)} data-testid={`admin-agency-check-${a.id}`} className="mt-1 h-5 w-5 shrink-0 accent-[var(--forest)]" />
              <div className="min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--forest)]">{LABEL[a.type]} · {a.city}</p>
                <p className="font-display-up text-lg">{a.name}</p>
                <p data-testid={`agency-status-${a.id}`} className="mt-1 flex flex-wrap gap-1.5 text-[11px] font-semibold">
                  {a.enabled === false ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-900">Sin habilitar · no se muestra</span> : <span className="rounded-full bg-[var(--lime)]/40 px-2 py-0.5">Habilitada</span>}
                  {a.commission_pct > 0 && <span className="rounded-full bg-black/5 px-2 py-0.5">Comisión {a.commission_pct}%</span>}
                  {a.code && <span className="rounded-full bg-black/5 px-2 py-0.5 font-mono">{a.code}</span>}
                </p>
                <p className="text-sm text-[var(--muted-ink)]">{a.address}{a.whatsapp ? ` · WA ${a.whatsapp}` : ""}</p>
                <p className={`mt-1 flex items-center gap-1.5 text-sm ${a.hours ? "text-[var(--ink)]" : "font-semibold text-amber-700"}`}><Clock className="h-3.5 w-3.5" /> {a.hours || "Sin horario cargado"}</p>
              </div>
              </label>
              <div className="flex gap-2">
                <button onClick={() => setEditing(a)} data-testid={`admin-agency-edit-${a.id}`} className="inline-flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 text-sm font-semibold"><Pencil className="h-4 w-4" /> Editar</button>
                <button onClick={() => remove(a)} data-testid={`admin-agency-delete-${a.id}`} aria-label="Eliminar" className="rounded-full border border-black/10 p-2.5 text-red-700"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <AgencyFormDialog open={editing !== undefined} agency={editing} onClose={() => setEditing(undefined)} onSaved={saved} />
    </section>
  );
}
